import {
  DvGridComponent,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DvNumberboxElement } from '@devinco/ui/legacy';
import { VippsPaymentDto } from '@pages/vipps-payments/interfaces';
import { DirectivesModule } from '@shared/directives/directives.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import { PaymentsGridService } from './payments-grid.service';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { Entity } from '@shared/constants/entity';
import { RestApiService } from '@core/services/rest-api.service';
import { EditDialogService } from '@shared/components/edit-dialog/edit-dialog.service';
import { FormGroup, Validators } from '@angular/forms';
import { of, switchMap, take, tap } from 'rxjs';
import { EditDialogData } from '@shared/components/edit-dialog/interfaces';
import { DvDialogComponent, dvNumberMaskPresets } from '@devinco/ui/dds';
import { EditDialogComponent } from '@shared/components/edit-dialog/edit-dialog.component';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarConfig } from '@shared/constants/snackbar-config';

@Component({
  selector: 'app-payments-grid',
  imports: [AgGridModule, DvGridComponent, DirectivesModule],
  templateUrl: './payments-grid.component.html',
  providers: [GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      height: 100%;
    }
  `,
})
export class PaymentsGridComponent {
  private gridService = inject(PaymentsGridService);
  private gridOptionsService = inject(GridOptionsService);
  private api = inject(RestApiService);
  private editDialogService = inject(EditDialogService);
  private snackbar = inject(DvSnackbarService);
  private translate = inject(TranslateService);

  actions: DvToolbarAction<VippsPaymentDto>[];
  gridOptions: GridOptions<VippsPaymentDto>;

  constructor() {
    this.actions = this.gridService.getGridActions(this.refundDonationFn);
    this.gridOptions = this.gridOptionsService.serverSide(
      this.gridService.getColumns(),
      Entity.VippsPayment
    );
  }

  private refundDonationFn = (
    event: DvToolbarActionEvent<VippsPaymentDto>
  ): void => {
    const data = event.api.getSelectedNodes()[0].data;
    const amount = data?.Amount;
    if (!amount) return;

    this.openPaymentRefundDialog(amount)
      .closed.pipe(
        switchMap(({ source, responseData }) => {
          if (source !== 'confirm') return of();

          return this.api.postCustom(
            `Route/donation/refund/${data.Reference}`,
            responseData.amount
          );
        }),
        take(1),
        tap(() =>
          this.snackbar.open(
            this.translate.instant('VippsPayment.RefundComplete'),
            SnackBarConfig.Success
          )
        )
      )
      .subscribe();
  };

  private openPaymentRefundDialog(amount: number): DvDialogComponent<
    EditDialogData<{
      amount: number;
    }>,
    EditDialogComponent<{
      amount: number;
    }>
  > {
    return this.editDialogService.openEditDialog({
      title: 'VippsPayment.RefundDialogTitle',
      elements: [
        new DvNumberboxElement({
          label: 'VippsPayment.RefundAmount',
          key: 'amount',
          colspan: 12,
          fieldMask: dvNumberMaskPresets.FormattedNumber,
          validators: [
            Validators.required,
            Validators.max(amount),
            Validators.min(1),
          ],
        }),
      ],
      entityData: { amount },
      afterReadyFn: (formGroup: FormGroup) =>
        formGroup.controls['amount'].markAsDirty(),
      type: 'warning',
    });
  }
}
